import React, { useState, useEffect, useRef } from 'react';
import { fetchFromApi, getMediaUrl } from '@utils/api';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledAboutSection = styled.section`
  max-width: 900px;

  .inner {
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-gap: 50px;

    @media (max-width: 768px) {
      display: block;
    }
  }
`;
const StyledText = styled.div`
  ul.skills-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(140px, 200px));
    grid-gap: 0 10px;
    padding: 0;
    margin: 20px 0 0 0;
    overflow: hidden;
    list-style: none;

    li {
      position: relative;
      margin-bottom: 10px;
      padding-left: 20px;
      font-family: var(--font-mono);
      font-size: var(--fz-xs);

      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--green);
        font-size: var(--fz-sm);
        line-height: 12px;
      }
    }
  }
`;
const StyledPic = styled.div`
  position: relative;
  max-width: 300px;

  @media (max-width: 768px) {
    margin: 50px auto 0;
    width: 70%;
  }

  .wrapper {
    ${({ theme }) => theme.mixins.boxShadow};
    display: block;
    position: relative;
    width: 100%;
    border-radius: var(--border-radius);
    background-color: var(--green);

    &:hover,
    &:focus {
      outline: 0;
      transform: translate(-4px, -4px);

      &:after {
        transform: translate(8px, 8px);
      }

      .img {
        filter: none;
        mix-blend-mode: normal;
      }
    }

    .img {
      position: relative;
      border-radius: var(--border-radius);
      mix-blend-mode: multiply;
      filter: grayscale(100%) contrast(1);
      transition: var(--transition);
    }

    &:before,
    &:after {
      content: '';
      display: block;
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius);
      transition: var(--transition);
    }

    &:before {
      top: 0;
      left: 0;
      background-color: var(--navy);
      mix-blend-mode: screen;
    }

    &:after {
      border: 2px solid var(--green);
      top: 14px;
      left: 14px;
      z-index: -1;
    }
  }
`;

const About = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  // Profile bio & avatar states
  const [bio, setBio] = useState(
    '<p>Hello! My name is Brittany and I enjoy creating things that live on the internet. My interest in web development started back in 2012 when I decided to try editing custom Tumblr themes — turns out hacking together a custom reblog button taught me a lot about HTML & CSS!</p>\n<p>Fast-forward to today, and I’ve had the privilege of working at <a href="https://us.mullenlowe.com/">an advertising agency</a>, <a href="https://starry.com/">a start-up</a>, <a href="https://www.apple.com/">a huge corporation</a>, and <a href="https://scout.camd.northeastern.edu/">a student-led design studio</a>. My main focus these days is building accessible, inclusive products and digital experiences at <a href="https://upstatement.com/">Upstatement</a> for a variety of clients.</p>\n<p>I also recently <a href="https://www.newline.co/courses/build-a-spotify-connected-app">launched a course</a> that covers everything you need to build a web app with the Spotify API using Node & React.</p>\n<p>Here are a few technologies I’ve been working with recently:</p>'
  );
  const [avatar, setAvatar] = useState('');
  const [avatarError, setAvatarError] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      // Load profile bio and avatar
      const profile = await fetchFromApi('/profile');
      if (profile) {
        if (profile.bio) setBio(profile.bio);
        if (profile.avatar) setAvatar(profile.avatar);
      }
    };
    loadData();

    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="numbered-heading">About Me</h2>

      <div className="inner">
        <StyledText>
          <div>
            <div dangerouslySetInnerHTML={{ __html: bio }} />
          </div>
        </StyledText>

        <StyledPic>
          <div className="wrapper">
            {avatar && !avatarError ? (
              <img 
                src={getMediaUrl(avatar)} 
                alt="Headshot" 
                className="img" 
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={() => {
                  setAvatarError(true);
                }}
              />
            ) : (
              <StaticImage
                className="img"
                src="../../images/me.jpg"
                width={500}
                quality={95}
                formats={['AUTO', 'WEBP', 'AVIF']}
                alt="Headshot"
              />
            )}
          </div>
        </StyledPic>
      </div>
    </StyledAboutSection>
  );
};

export default About;
